/**
 * ErrorBoundary — Catches chunk load failures and render errors.
 * Shows a user-friendly reload prompt instead of a blank screen.
 * Auto-resets on navigation when wrapped inside a Router (via LocationAwareErrorBoundary).
 */
import { Component, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { forceRefresh } from "@/lib/buildVersion";

interface Props {
  children: ReactNode;
  locationKey?: string;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

class ErrorBoundaryInner extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    const isChunkError =
      error.message?.includes("Importing a module script failed") ||
      error.message?.includes("Failed to fetch dynamically imported module") ||
      error.message?.includes("Loading chunk") ||
      error.message?.includes("Loading CSS chunk");

    return { hasError: true, isChunkError };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary] caught:", error.message, {
      stack: info.componentStack?.slice(0, 500),
    });
  }

  componentDidUpdate(prevProps: Props) {
    // Auto-reset on navigation for non-chunk errors (transient render errors during route transitions)
    if (
      this.state.hasError &&
      !this.state.isChunkError &&
      prevProps.locationKey !== this.props.locationKey
    ) {
      this.setState({ hasError: false, isChunkError: false });
    }
  }

  handleReload = () => {
    forceRefresh();
  };

  handleGoHome = () => {
    window.location.href = "/dashboard";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-4 text-center">
          <div className="rounded-xl border border-border bg-card p-8 max-w-sm w-full space-y-4">
            <p className="text-4xl">{this.state.isChunkError ? "⚡" : "🛸"}</p>
            <h2 className="text-lg font-semibold text-foreground">
              {this.state.isChunkError
                ? "Mise à jour disponible"
                : "Oups, un petit souci !"}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {this.state.isChunkError
                ? "L'application a été mise à jour. Recharge la page pour continuer."
                : "Quelque chose s'est mal passé, mais l'app est toujours là. Tu peux recharger ou revenir au tableau de bord."}
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={this.handleReload}
                className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Recharger la page
              </button>
              {!this.state.isChunkError && (
                <button
                  onClick={this.handleGoHome}
                  className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors"
                >
                  Retour au tableau de bord
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground/70 pt-1">
              Si le problème persiste, n'hésite pas à recharger une seconde fois. 🛠️
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Global wrapper — works outside Router (e.g. in main.tsx).
 * Does not auto-reset on navigation since there's no Router context.
 */
export function ErrorBoundaryGlobal({ children }: { children: ReactNode }) {
  return <ErrorBoundaryInner>{children}</ErrorBoundaryInner>;
}

/**
 * Location-aware wrapper — auto-resets on navigation.
 * Must be used inside a <BrowserRouter>.
 */
export function ErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();

  return (
    <ErrorBoundaryInner locationKey={location.key}>
      {children}
    </ErrorBoundaryInner>
  );
}
